import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Film, Wand2, CheckCircle2, Send, Plug, Upload, ArrowRight, ShieldAlert } from "lucide-react";

const platformMeta = {
  youtube: { label: "YouTube", color: "#FF0000" },
  instagram: { label: "Instagram", color: "#E1306C" },
  facebook: { label: "Facebook", color: "#1877F2" },
};

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/projects").then((r) => setProjects(r.data.slice(0, 5)));
    api.get("/connections").then((r) => setConnections(r.data));
  }, []);

  const cards = [
    { label: "Source Reels", value: stats?.assets ?? "–", icon: Film, testId: "stat-assets" },
    { label: "Projects", value: stats?.projects ?? "–", icon: Wand2, testId: "stat-projects" },
    { label: "Ready to Publish", value: stats?.ready ?? "–", icon: CheckCircle2, testId: "stat-ready" },
    { label: "Published", value: stats?.published ?? "–", icon: Send, testId: "stat-published" },
  ];

  return (
    <div className="rf-grid-bg min-h-screen p-10">
      <div className="rf-fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF]">Workspace</p>
        <h1 className="mt-1 font-heading text-4xl font-bold">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="mt-2 text-gray-400">Your reel branding & publishing control center.</p>
      </div>

      {!isAdmin && (
        <div data-testid="editor-notice" className="mt-6 flex items-center gap-2 rounded-md border border-[#FF007F]/30 bg-[#FF007F]/10 px-4 py-3 text-sm text-[#FF007F]">
          <ShieldAlert className="h-4 w-4" />
          Editor role: you can edit & preview projects. Downloads, exports and publishing are disabled.
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 gap-5 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testId} className="rounded-lg border border-white/10 bg-[#121212] p-5 transition-transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <c.icon className="h-5 w-5 text-[#00E5FF]" />
            </div>
            <div className="mt-6 font-heading text-3xl font-bold">{c.value}</div>
            <div className="mt-1 text-sm text-gray-400">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-lg border border-white/10 bg-[#121212] p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Recent Projects</h3>
            <button onClick={() => navigate("/library")} data-testid="view-library-btn" className="flex items-center gap-1 text-sm text-[#00E5FF] hover:underline">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {projects.length === 0 ? (
            <EmptyProjects onImport={() => navigate("/import")} />
          ) : (
            <div className="space-y-2">
              {projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/editor/${p.id}`)}
                  data-testid={`recent-project-${p.id}`}
                  className="flex w-full items-center justify-between rounded-md border border-white/5 bg-[#0f0f0f] px-4 py-3 text-left transition-colors hover:border-[#00E5FF]/30"
                >
                  <span className="truncate font-medium">{p.name}</span>
                  <StatusBadge status={p.status} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-white/10 bg-[#121212] p-6">
          <h3 className="mb-4 font-heading text-lg font-semibold">Connected Accounts</h3>
          <div className="space-y-3">
            {["youtube", "instagram", "facebook"].map((p) => {
              const conn = connections.find((c) => c.platform === p && c.connected);
              return (
                <div key={p} className="flex items-center justify-between rounded-md border border-white/5 bg-[#0f0f0f] px-3 py-2.5">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: platformMeta[p].color }} />
                    {platformMeta[p].label}
                  </span>
                  <span className={`text-xs ${conn ? "text-[#34C759]" : "text-gray-500"}`}>
                    {conn ? conn.account_name : "Not connected"}
                  </span>
                </div>
              );
            })}
          </div>
          <button onClick={() => navigate("/publish")} data-testid="manage-connections-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-white/10 py-2.5 text-sm font-medium hover:bg-white/5">
            <Plug className="h-4 w-4" /> Manage connections
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyProjects({ onImport }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <Upload className="h-10 w-10 text-gray-600" />
      <p className="mt-3 text-sm text-gray-400">No projects yet. Import a reel to start branding.</p>
      <button onClick={onImport} data-testid="empty-import-btn" className="mt-4 rounded-md bg-[#00E5FF] px-4 py-2 text-sm font-semibold text-black hover:bg-[#33EBFF]">
        Import Video
      </button>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    draft: { c: "text-gray-400 border-gray-600 bg-gray-500/10", t: "Draft" },
    processing: { c: "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/10", t: "Processing" },
    ready: { c: "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10", t: "Ready" },
    failed: { c: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10", t: "Failed" },
  };
  const s = map[status] || map.draft;
  return <span className={`rounded border px-2 py-0.5 text-xs font-medium ${s.c}`}>{s.t}</span>;
}
